import React from 'react';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { MagnifyingGlass, X } from '@phosphor-icons/react';
import DatePicker from '@/components/DatePicker';

/**
 * Shared search + filter bar. Client-side friendly.
 *
 * Props:
 *  - keyword, onKeywordChange
 *  - statusValue, onStatusChange, statusOptions: [{value, label}]  (optional)
 *  - startDate, endDate, onStartDateChange, onEndDateChange  (optional)
 *  - onClear: called when the user hits "Clear"
 *  - placeholder: string for the keyword input
 *  - testIdPrefix: prefix used for data-testids
 */
const SearchFilterBar = ({
  keyword,
  onKeywordChange,
  statusValue,
  onStatusChange,
  statusOptions,
  startDate,
  endDate,
  onStartDateChange,
  onEndDateChange,
  onClear,
  placeholder = 'Search…',
  testIdPrefix = 'filter',
}) => {
  const hasStatus = Array.isArray(statusOptions) && statusOptions.length > 0;
  const hasDates = typeof onStartDateChange === 'function' || typeof onEndDateChange === 'function';
  const showClear = keyword || (statusValue && statusValue !== 'all') || startDate || endDate;

  return (
    <div className="flex flex-wrap items-end gap-3 p-3 bg-surface border border-border-default rounded">
      <div className="flex-1 min-w-[220px]">
        <div className="relative">
          <MagnifyingGlass size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-text-tertiary" />
          <Input
            value={keyword || ''}
            onChange={(e) => onKeywordChange(e.target.value)}
            placeholder={placeholder}
            className="pl-9 h-9"
            data-testid={`${testIdPrefix}-keyword`}
          />
        </div>
      </div>

      {hasStatus && (
        <div className="min-w-[160px]">
          <Select value={statusValue || 'all'} onValueChange={onStatusChange}>
            <SelectTrigger className="h-9" data-testid={`${testIdPrefix}-status`}>
              <SelectValue placeholder="Status" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All statuses</SelectItem>
              {statusOptions.map((s) => (
                <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      )}

      {hasDates && (
        <div className="flex items-end gap-2">
          <div>
            <label className="text-xs text-text-tertiary block mb-1">From</label>
            <DatePicker
              value={startDate || ''}
              onChange={(v) => onStartDateChange && onStartDateChange(v)}
              placeholder="Start date"
              testId={`${testIdPrefix}-start-date`}
              maxDate={endDate || undefined}
            />
          </div>
          <div>
            <label className="text-xs text-text-tertiary block mb-1">To</label>
            <DatePicker
              value={endDate || ''}
              onChange={(v) => onEndDateChange && onEndDateChange(v)}
              placeholder="End date"
              testId={`${testIdPrefix}-end-date`}
              minDate={startDate || undefined}
            />
          </div>
        </div>
      )}

      {showClear && (
        <button
          type="button"
          onClick={onClear}
          className="h-9 px-3 text-sm text-text-secondary hover:text-accent-brand flex items-center gap-1"
          data-testid={`${testIdPrefix}-clear`}
        >
          <X size={14} /> Clear
        </button>
      )}
    </div>
  );
};

/**
 * Utility: filter an array of records against the SearchFilterBar state.
 * Pass `fields` to specify which record properties are matched by the keyword.
 * Pass `dateField` to specify the ISO datetime field for date-range filtering.
 */
export const filterRecords = (records, { keyword, statusValue, startDate, endDate, fields = [], statusField = 'status', dateField = 'created_at' }) => {
  const kw = (keyword || '').trim().toLowerCase();
  const s = startDate ? new Date(`${startDate}T00:00:00`) : null;
  const e = endDate ? new Date(`${endDate}T23:59:59`) : null;

  return records.filter((r) => {
    if (statusValue && statusValue !== 'all' && r[statusField] !== statusValue) return false;
    if (kw) {
      const hit = fields.some((f) => {
        const v = r[f];
        return v != null && String(v).toLowerCase().includes(kw);
      });
      if (!hit) return false;
    }
    if (s || e) {
      const raw = r[dateField];
      if (!raw) return false;
      const d = new Date(raw);
      if (s && d < s) return false;
      if (e && d > e) return false;
    }
    return true;
  });
};

export default SearchFilterBar;
